<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import RedSpot from '@/components/RedSpot.vue';
import PageCard from '@/components/cards/PageCard.vue';
import PageInnerCard from '@/components/cards/PageInnerCard.vue';
import PageCardSlider from '@/components/PageCardSlider.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  template,
  columnsCount,
  title,
  description,
  items,
  hasTitle,
  hasDescription,
  hasItems,
  hasAnyContent,
  redSpot,
  hasRedSpot,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <RedSpot
      v-if="hasRedSpot"
      :position-vertical="redSpot.vertical"
      :position-horizontal="redSpot.horizontal"
    />
    <div class="container">
      <div :class="[`pages-cards pages-cards--${template}`]">
        <div
          :class="[
            'pages-cards__top',
            backgroundColor === 'black' ? 'white' : 'black',
          ]"
        >
          <h2
            v-if="hasTitle"
            class="mb-0 pages-cards__title h2 text-center"
            data-aos="fade-in"
            data-aos-delay="100"
            v-html="title"
          ></h2>
          <div
            v-if="hasDescription"
            class="pages-cards__description body-1-regular text-center"
            data-aos="fade-in"
            data-aos-delay="150"
            v-html="description"
          ></div>
        </div>
        <template v-if="hasItems">
          <PageCardSlider
            v-if="template === 'slider'"
            :items="items"
            :background-color="backgroundColor"
          />
          <div
            v-else
            :class="[`pages-cards__items pages-cards__items--${columnsCount}`]"
          >
            <template v-for="(item, itemIndex) in items" :key="item.title">
              <PageInnerCard
                v-if="template === 'inner'"
                data-aos="fade-up"
                :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
                :url="item?.url"
                :image="item?.image"
                :title="item?.title"
                :description="item?.description"
                :background-color="backgroundColor"
              />
              <PageCard
                v-else
                data-aos="fade-up"
                :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
                :url="item?.url"
                :image="item?.image"
                :title="item?.title"
                :description="item?.description"
                :children="item?.children"
              />
            </template>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.pages-cards {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__title {
    margin: 0;
  }
  &__description {
    &:deep(p:last-child) {
      margin: 0;
    }
    @include media-breakpoint-up(sm) {
      max-width: 86%;
    }
    @include media-breakpoint-up(md) {
      max-width: 66.3%;
    }
  }
  &__items {
    display: grid;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 6fr);
    }
    @include media-breakpoint-up(md) {
      gap: 16px;
      &--3 {
        grid-template-columns: repeat(3, 1fr);
      }
      &--4 {
        grid-template-columns: repeat(4, 1fr);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &--slider {
    #{$parent} {
      &__title {
        @include media-breakpoint-up(md) {
          margin: 0 auto;
          max-width: 66.3%;
          text-align: center;
        }
      }
    }
  }
  &--inner {
    #{$parent} {
      &__title {
        @include media-breakpoint-up(md) {
          margin: 0 auto;
          max-width: 66.3%;
        }
      }
      &__items {
        @include media-breakpoint-up(md) {
          gap: 8px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }
    }
  }
}
</style>
